import { useEffect, useMemo, useState } from 'react'
import { getErrorMessage } from '@features/shared/api/client'
import { DisabledReason } from '@features/shared/components/DisabledReason'
import { FieldInput } from '@features/shared/components/FieldInput'
import { ModalShell } from '@features/shared/components/ModalShell'
import { useToast } from '@features/shared/components/Toast'
import { useMicrosoftSenders } from '@features/automations/hooks/useMicrosoftSenders'
import { microsoftSenderOptions } from '@features/automations/logic/microsoftSenderOptions'
import { useSendingAddresses } from '@features/email/hooks/useSendingAddresses'
import { sendExports } from '../api/exports.api'
import type { ExportJob } from '../api/contracts'
import { defaultSubject, fileSizeWords, SEND_MAX_BYTES } from '../logic/sendExport'
import { useSendExportStore, type SendExportDraft } from '../stores/useSendExportStore'

/**
 * From, through Arvoke: the person's own address when its domain is one of
 * the organization's sending domains that can send (Settings > Email), else
 * Arvoke's address named for them. The server decides as it sends.
 */
const ARVOKE = 'arvoke'

/**
 * Send: one or more finished exports emailed as attachments, from the
 * person's own Microsoft 365 mailbox (or one they may send as) when the
 * organization is connected, or from Arvoke's address named for them. The
 * server checks the mailbox in Exchange as it sends. Lives in the app shell
 * (`useSendExportStore`), so closing the menu that opened it doesn't close it.
 */
export default function SendExportDialog() {
  const exports = useSendExportStore((state) => state.exports)
  const draft = useSendExportStore((state) => state.draft)
  const close = useSendExportStore((state) => state.close)
  if (!exports || exports.length === 0) return null
  // A new set of files (or a new draft of them) starts a new form.
  return (
    <SendExportForm
      key={`${exports.map((job) => job.export_id).join(',')}:${draft ? draft.to.join(',') : ''}`}
      exports={exports}
      draft={draft}
      onClose={close}
    />
  )
}

function SendExportForm({
  exports,
  draft,
  onClose,
}: {
  exports: ExportJob[]
  /** What the AI Assistant filled in, for the person to check and send. */
  draft: SendExportDraft | null
  onClose: () => void
}) {
  const { addToast } = useToast()
  const { senders, loading } = useMicrosoftSenders()
  const ownAddress = useSendingAddresses().data?.own_address ?? null
  const own = senders.find((sender) => sender.is_own_mailbox)
  const [from, setFrom] = useState<string | null>(draft?.fromMailbox ?? null)
  // Until the person picks, their own mailbox once it's known, else Arvoke.
  const chosen = from ?? own?.sender_email ?? ARVOKE
  const [to, setTo] = useState<string[]>(draft?.to ?? [])
  const [toPending, setToPending] = useState<string[]>([])
  const [cc, setCc] = useState<string[]>(draft?.cc ?? [])
  const [ccPending, setCcPending] = useState<string[]>([])
  const [subject, setSubject] = useState(() => draft?.subject || defaultSubject(exports))
  const [message, setMessage] = useState(draft?.message ?? '')
  const [sending, setSending] = useState(false)

  const options = useMemo(
    () => [
      ...microsoftSenderOptions(senders).map((option) => ({
        value: option.id,
        label: option.label,
        description: [option.description, option.secondaryLabel].filter(Boolean).join(' · '),
      })),
      {
        value: ARVOKE,
        label: 'Arvoke',
        description: ownAddress
          ? `From ${ownAddress}, sent through Arvoke`
          : "Arvoke's address, with your name; replies come to you",
      },
    ],
    [ownAddress, senders]
  )
  // A mailbox that's gone from the list (its rights changed) falls back.
  useEffect(() => {
    if (from && !options.some((option) => option.value === from)) setFrom(null)
  }, [from, options])

  const total = exports.reduce((sum, job) => sum + (job.file_size ?? 0), 0)
  const recipients = [...to, ...toPending]
  const copies = [...cc, ...ccPending]
  // The assistant's outside recipients still in the email: the person sees
  // who outside the organization gets it before sending.
  const outside = (draft?.outsideRecipients ?? []).filter(
    (address) => recipients.includes(address) || copies.includes(address)
  )
  const blocked =
    total > SEND_MAX_BYTES
      ? `${exports.length > 1 ? 'These files are' : 'This file is'} ${fileSizeWords(total)}; an email takes ${fileSizeWords(SEND_MAX_BYTES)}`
      : recipients.length === 0
        ? 'Add who to send it to'
        : !subject.trim()
          ? 'Add a subject'
          : null

  const send = async () => {
    if (blocked || sending) return
    setSending(true)
    try {
      const result = await sendExports({
        export_ids: exports.map((job) => job.export_id),
        to: recipients,
        cc: copies,
        subject: subject.trim(),
        message: message.trim() || null,
        from_mailbox: chosen === ARVOKE ? null : chosen,
      })
      addToast({
        type: 'success',
        title: 'Sent',
        message: result.test
          ? `Outside production, email goes only to the test address: sent to ${result.sent_to.join(', ')}.`
          : `Sent to ${recipients.join(', ')}.`,
      })
      onClose()
    } catch (error) {
      addToast({ type: 'error', title: "Couldn't send", message: getErrorMessage(error) })
      setSending(false)
    }
  }

  return (
    <ModalShell
      open
      title={exports.length > 1 ? `Send ${exports.length} exports` : 'Send export'}
      description="Emailed as an attachment."
      onClose={onClose}
      width="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-default surface-default px-3 py-1.5 text-xs font-medium text-default transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <DisabledReason reason={blocked}>
            <button
              type="button"
              onClick={() => void send()}
              disabled={Boolean(blocked) || sending}
              className="rounded-md bg-[color:var(--action-brand-bg)] px-3 py-1.5 text-xs font-medium text-[color:var(--action-brand-text)] transition-colors hover:bg-[color:var(--action-brand-bg-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </DisabledReason>
        </div>
      }
    >
      <div className="space-y-3 p-4">
        {draft ? (
          <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted">
            The AI Assistant filled this in. Check who it goes to and what's attached: nothing is
            sent until you click Send.
          </p>
        ) : null}
        <FieldInput
          type="select"
          label="From"
          value={chosen}
          // Only the person's pick counts: a list of one (Arvoke, before their
          // mailboxes load) picks itself, which mustn't hold From there.
          onChange={(value) => {
            if (value !== chosen) setFrom(value)
          }}
          options={options}
          required
          allowEmpty={false}
          placeholder={loading ? 'Loading your mailboxes…' : undefined}
        />
        <FieldInput
          type="email-list"
          label="To"
          value={to}
          onChange={setTo}
          onPendingChange={setToPending}
          required
        />
        {outside.length > 0 ? (
          <p className="rounded-md surface-warning-subtle px-3 py-2 text-xs text-warning">
            Outside your organization: {outside.join(', ')}
          </p>
        ) : null}
        <FieldInput
          type="email-list"
          label="CC"
          value={cc}
          onChange={setCc}
          onPendingChange={setCcPending}
        />
        <FieldInput
          type="short-text"
          label="Subject"
          value={subject}
          onChange={setSubject}
          required
        />
        <FieldInput
          type="long-text"
          label="Message"
          value={message}
          onChange={setMessage}
          placeholder="Add a note (the files are listed below it)"
        />
        <div>
          <p className="text-xs font-[450] text-muted">Attached</p>
          <ul className="mt-1 space-y-0.5">
            {exports.map((job) => (
              <li key={job.export_id} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate text-default">{job.file_name}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted">
                  {job.file_size ? fileSizeWords(job.file_size) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ModalShell>
  )
}
